import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOnboardingBlocked } from "@/pages/onboarding/OnboardingStep";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Settings,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  AutoFlowBrandColContent,
  AutoFlowFormColContent,
} from "@/components/onboarding/OnboardingAutoFlowInline";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { getDgiConfigTrail } from "@/components/onboarding/OnboardingTrail";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";
import { DgiConsultaFlowModal } from "@/pages/dgi-documents/dgi-consulta-flow-modal";
import { useOnboardingAutoFlow } from "@/pages/onboarding/useOnboardingAutoFlow";
import { createDgiPortalUser } from "@/services/authService";
import { registerIngresoSimplificado } from "@/services/ingresoSimplificadoService";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  clearDgiOnboardingSession,
  clearDgiPortalPasswordFromSession,
  readDgiManualFallbackRawFromSession,
  readDgiPortalPasswordFromSession,
  writeDgiManualFallbackToSession,
  writeDgiPortalPasswordToSession,
} from "@/pages/onboarding/dgi-onboarding-session";

const DGI_SERVICIOS_URL = "https://servicios.dgi.gub.uy/serviciosenlinea";
const DGI_NOTIFICATIONS_EMAIL = "notificaciones-dgi@fiscalo.uy";

/** Default doc type for the technical DGI portal user. The cédula itself is the tenant owner's personal CI (read live from AuthContext). */
const DGI_EFACTURA_PORTAL_USER_DOC_TYPE = "CI" as const;

export interface DgiAccessStepProps {
  legalName: string;
  tenantId?: string | null;
  hasDgiPortalUser?: boolean;
  onContinue: () => void;
  onBack?: () => void;
  onPasswordReady?: () => void;
  onStepsCompletedConfirmed?: (value: boolean) => void;
  stepsCompletedConfirmed?: boolean;
  dgiPortalUserConfirmed?: boolean;
  dgiStep3SuccessToastAcknowledged?: boolean;
  onRefreshTenant?: () => void;
  /** Per-step completion timestamps from the tenant — lets refresh resume. */
  dgiAutoOnboardingProgress?: Record<string, unknown> | null;
  /** When true, the trail shows a 4th "CFE anteriores" step and step
   *  tags report "step 02 de 04" instead of "02 de 03". */
  hasCfeImportNext?: boolean;
  /** The user's chosen situation — drives whether the trail surfaces
   *  a "Certificado" 4th step (scratch / regularization paths). */
  situationChoice?: SituationChoice | null;
}

interface CredentialFieldProps {
  k: string;
  v: string;
  hide?: boolean;
  toggleVisible?: () => void;
  visible?: boolean;
  onCopy: () => void;
  successKey: string;
}

function CredentialField({
  k,
  v,
  hide,
  toggleVisible,
  visible,
  onCopy,
}: CredentialFieldProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    onCopy();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="fx-onb-cred">
      <div className="body">
        <span className="key">{k}</span>
        <span className="value">{hide && !visible ? "••••••••" : v}</span>
      </div>
      {toggleVisible ? (
        <button
          type="button"
          onClick={toggleVisible}
          aria-label={visible ? "Hide" : "Show"}
        >
          {visible ? <EyeOff /> : <Eye />}
        </button>
      ) : null}
      <button
        type="button"
        onClick={handleCopy}
        className={cn(copied && "copied")}
        aria-label="Copy"
      >
        {copied ? <Check /> : <Copy />}
      </button>
    </div>
  );
}

export function DgiAccessStep({
  legalName,
  tenantId = null,
  hasDgiPortalUser = false,
  onContinue,
  onBack,
  onPasswordReady,
  onStepsCompletedConfirmed,
  stepsCompletedConfirmed = false,
  dgiPortalUserConfirmed = false,
  dgiStep3SuccessToastAcknowledged = false,
  onRefreshTenant,
  dgiAutoOnboardingProgress = null,
  hasCfeImportNext = false,
  situationChoice = null,
}: DgiAccessStepProps): JSX.Element {
  const { t } = useTranslation();
  const reportBlocked = useOnboardingBlocked();
  const dgiConfigTrailSteps = getDgiConfigTrail({
    situationChoice,
    isMigratingCfes: hasCfeImportNext,
    // Scratch reaches this step ONLY pre-homologación: once the user registers
    // (handleScratchManualDone) the pending gate takes over, and the
    // post-homologación resume routes to the cert screens, never back here. So
    // the trail here always shows the pre-homologación 3-row "→ Homologación
    // (team)" framing rather than dangling Modo/Certificado.
    homologacionPending: situationChoice === "scratch",
  });
  const dgiConfigTotal = dgiConfigTrailSteps.length;
  // Where the "Configuración DGI" row sits in the sub-flow trail depends on the
  // cert ordering:
  //   - scratch (DGI-first): DGI is row 2 — right after Situación, before the
  //     certificate phase.
  //   - regularization (cert-first): DGI is the final row (row 4); the cert was
  //     requested up front.
  //   - already_issuer / null: the legacy linear numbering.
  const isCertBeforeDgi =
    situationChoice === "scratch" ||
    situationChoice === "issuer_need_regularization";
  const isScratch = situationChoice === "scratch";
  // Existing emisores (already invoicing, or lapsed & regularizing) create the
  // Fiscalo e-Factura user with BOTH roles up front — they are not doing a
  // homologación, so "Producción" isn't a later step. Scratch keeps the
  // homologación-only role.
  const isExistingEmisor =
    situationChoice === "already_issuer" ||
    situationChoice === "issuer_need_regularization";
  const dgiTagStep = isScratch ? 2 : isCertBeforeDgi ? 4 : 3;
  const dgiTrailCurrent = isScratch ? 2 : isCertBeforeDgi ? 4 : 2;
  const { user } = useAuth();
  // The DGI technical portal user is registered under the tenant owner's personal CI (not a
  // pooled Fiscalo CI anymore). If documentNumber is missing from the user's profile, the
  // backend will reject createDgiPortalUser with a clear message; we surface a placeholder
  // here so the credentials card stays readable in that pre-condition state.
  const portalUserCedula = user?.documentNumber?.trim() || "";
  const [generatedPassword, setGeneratedPasswordState] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState<"automation" | "regenerate" | null>(
    null,
  );
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [choseAutomatic, setChoseAutomatic] = useState(false);
  const [manualFallback, setManualFallbackState] = useState(false);
  // Entry-card selection: let Fiscalo run the auto-flow, or do it yourself in
  // the DGI portal (routes to the existing step-by-step manual guide). Defaults
  // to the recommended auto path so the common case stays a single click.
  const [dgiSetupChoice, setDgiSetupChoice] = useState<"auto" | "manual">(
    "auto",
  );

  // Whether the tenant's persisted progress map already shows the auto-flow
  // as complete. Acts as the authoritative "auto-flow is done" signal across
  // refreshes / new tabs (sessionStorage flags can't be trusted on first
  // render — they're hydrated by a separate useEffect). Scratch tenants
  // don't run proveedor_software, so that key is excluded for them.
  const allAutoStepsDone =
    Boolean(dgiAutoOnboardingProgress?.portal_user) &&
    Boolean(dgiAutoOnboardingProgress?.company_info) &&
    Boolean(dgiAutoOnboardingProgress?.cae_download) &&
    (situationChoice === "scratch" ||
      Boolean(dgiAutoOnboardingProgress?.proveedor_software));

  // Drives the manual-fallback view: when the auto-flow stalled on a
  // step other than portal_user, the manual instructions need to target
  // *that* step (e.g. "Configurar Fiscalo como tu proveedor de e-Factura") instead of the
  // default "create the e-Factura user" copy. Scratch tenants skip
  // proveedor_software entirely, so it's dropped from the order for
  // them too — otherwise this helper would forever report it as the
  // first incomplete step.
  // last_numbers is gone: the CAE step files a fresh solicitud and the backend
  // retires every pre-existing range, so there is no resume point to discover.
  // Leaving it here would have pinned allAutoStepsDone to false forever and
  // made this helper report it as the first incomplete step on every render.
  const AUTO_STEP_ORDER = [
    "portal_user",
    "company_info",
    "cae_download",
    "proveedor_software",
  ] as const;
  type AutoStepKey = (typeof AUTO_STEP_ORDER)[number];
  const firstIncompleteAutoStep: AutoStepKey | null = (() => {
    const progress = dgiAutoOnboardingProgress ?? {};
    for (const key of AUTO_STEP_ORDER) {
      if (key === "proveedor_software" && situationChoice === "scratch") {
        continue;
      }
      if (!progress[key]) return key;
    }
    return null;
  })();

  // Durable "the automatic portal_user step already failed" signal, read from
  // the tenant's persisted auto-onboarding progress (survives reload, unlike the
  // sessionStorage manualFallback flag). `_failures.portal_user` is the retry
  // count (>0 after the first stall — matches the in-session onFailed trigger);
  // `_failed.portal_user` is the terminal give-up message. Used only to SEED the
  // manual-fallback view on return, so a tenant whose automation stalled reliably
  // lands on the manual steps — while still being able to switch back to
  // automatic (that explicit choice is persisted and wins over this seed).
  const autoPortalUserFailedSeed = (() => {
    if (dgiPortalUserConfirmed) return false;
    if (dgiAutoOnboardingProgress?.portal_user) return false;
    const p = dgiAutoOnboardingProgress as
      | { _failed?: Record<string, unknown>; _failures?: Record<string, number> }
      | null;
    return (
      Boolean(p?._failed?.portal_user) ||
      Number(p?._failures?.portal_user ?? 0) > 0
    );
  })();

  // Scratch tenants haven't yet been homologated by DGI as e-Factura
  // emisores, so Fiscalo can't be registered as their proveedor de
  // software via the automation — that registration only opens up
  // after the slow DGI homologation process the user has to run by
  // hand. Skip the proveedor_software step for them; the rest of the
  // chain (portal_user → company_info → cae_download)
  // still runs end-to-end.
  const skipProveedorSoftware = situationChoice === "scratch";

  const flow = useOnboardingAutoFlow({
    portalUserAlreadyDone: dgiPortalUserConfirmed,
    completedSteps: dgiAutoOnboardingProgress as
      | Partial<Record<"portal_user" | "company_info" | "cae_download" | "proveedor_software", string | null>>
      | undefined,
    skipProveedorSoftware,
    onAllComplete: async () => {
      onStepsCompletedConfirmed?.(true);
      await onRefreshTenant?.();
    },
    onFailed: () => {
      setManualFallback(true);
      onStepsCompletedConfirmed?.(false);
    },
  });

  const persistPassword = useCallback(
    (password: string | null): void => {
      setGeneratedPasswordState(password);
      if (!tenantId) return;
      if (password) {
        writeDgiPortalPasswordToSession(tenantId, password);
      } else {
        clearDgiPortalPasswordFromSession(tenantId);
      }
    },
    [tenantId],
  );

  const setManualFallback = useCallback(
    (value: boolean): void => {
      setManualFallbackState(value);
      if (tenantId) {
        writeDgiManualFallbackToSession(tenantId, value);
      }
    },
    [tenantId],
  );

  // True once the manualFallback state has been hydrated from sessionStorage
  // for the current tenant. The auto-resume effect gates on this so a stale
  // first-render `manualFallback=false` (before hydration) doesn't trigger
  // an unwanted auto-resume for users who already opted into manual.
  const [sessionHydrated, setSessionHydrated] = useState(false);
  useEffect(() => {
    setSessionHydrated(false);
    if (!tenantId) {
      setGeneratedPasswordState(null);
      setManualFallbackState(false);
      setSessionHydrated(true);
      return;
    }
    setGeneratedPasswordState(readDgiPortalPasswordFromSession(tenantId));
    // No explicit choice yet (null) → seed from the durable auto-fail signal so
    // the manual steps show on return; an explicit "1"/"0" always wins.
    setManualFallbackState(
      readDgiManualFallbackRawFromSession(tenantId) ?? autoPortalUserFailedSeed,
    );
    setSessionHydrated(true);
  }, [tenantId, autoPortalUserFailedSeed]);

  useEffect(() => {
    if (dgiPortalUserConfirmed && tenantId) {
      clearDgiOnboardingSession(tenantId);
    }
  }, [dgiPortalUserConfirmed, tenantId]);

  /**
   * Whether the auto-flow is paused mid-chain on this tenant — at least one
   * step is already persisted (`dgiAutoOnboardingProgress.portal_user`) but
   * not all five are complete. We surface a dedicated "Reanudar
   * configuración" view in this case so the user has to explicitly opt back
   * into the automation instead of having it kick off silently on refresh.
   */
  const hasInFlightAutomation =
    Boolean(dgiAutoOnboardingProgress?.portal_user) &&
    !allAutoStepsDone &&
    !manualFallback &&
    !choseAutomatic;

  const handleResumeAutomation = useCallback((): void => {
    setChoseAutomatic(true);
    void flow.start();
  }, [flow]);

  // Auto-resume on refresh: when portal_user is done and steps 2-5 are still
  // pending (the common "user reloaded /onboarding mid-chain" case), don't
  // strand them on the chooser/Reanudar screen. Re-enter the inline auto-flow
  // view straight away and trigger flow.start() — the backend's per-step
  // session deduplication will reuse the in-flight Python session if one is
  // running, so this is safe to fire on every mount.
  const autoResumeFiredRef = useRef(false);
  useEffect(() => {
    if (autoResumeFiredRef.current) return;
    if (!sessionHydrated) return;
    if (!hasInFlightAutomation) return;
    autoResumeFiredRef.current = true;
    handleResumeAutomation();
  }, [sessionHydrated, hasInFlightAutomation, handleResumeAutomation]);

  const hasPortalCredentials = Boolean(generatedPassword || hasDgiPortalUser);
  const showMinimalPrimaryOnly =
    !manualFallback && !dgiPortalUserConfirmed && !stepsCompletedConfirmed;
  // Scratch (brand-new emisor) is MANUAL-ONLY: full DGI homologación can't be
  // automated, so the user always creates the e-Factura user themselves via the
  // step-by-step instructions, then we register the homologación and hand it to
  // the team. (The auto/manual choice is skipped for scratch.)
  const showManualInstructions = manualFallback || isScratch;
  const showWaitingForDgiEmail =
    stepsCompletedConfirmed && !dgiPortalUserConfirmed && !manualFallback;
  // Scratch tenants have no pre-existing e-Factura user to "ya lo hice" — they
  // must actually create + have it confirmed before continuing. Block the
  // manual handoff escape for them (the parent gate enforces this too; this
  // keeps the button from being a silent no-op).
  const blockScratchHandoff =
    situationChoice === "scratch" && !dgiPortalUserConfirmed;
  const canGenerateNewPortalUser =
    !hasDgiPortalUser && !generatedPassword && !saving;

  const handleStartAutomation = async (): Promise<void> => {
    if (!hasPortalCredentials) {
      if (!canGenerateNewPortalUser) return;
      setSaving(true);
      setSavingAction("automation");
      try {
        const { password } = await createDgiPortalUser();
        persistPassword(password);
        onPasswordReady?.();
        await onRefreshTenant?.();
      } catch {
        reportBlocked("dgi_portal_user_create_failed");
        toast.error(t("onboarding.dgiAccessError"));
        return;
      } finally {
        setSaving(false);
        setSavingAction(null);
      }
    }
    setChoseAutomatic(true);
    await flow.start();
  };

  /**
   * Scratch manual completion: the user created the DGI e-Factura user themselves
   * and ticked "hice todos los pasos". Register the homologación (no automation)
   * and refresh — the parent's needsHomologacionPending gate then parks them on
   * the "homologación pendiente" screen while the team finishes homologación.
   */
  const handleScratchManualDone = useCallback(async (): Promise<void> => {
    setSaving(true);
    try {
      await registerIngresoSimplificado();
      await onRefreshTenant?.();
    } catch {
      reportBlocked("ingreso_simplificado_register_failed");
      toast.error(t("onboarding.dgiAccessError"));
    } finally {
      setSaving(false);
    }
  }, [onRefreshTenant, reportBlocked, t]);

  // Scratch handoff is gated on the REAL DGI validation: only once DGI has
  // confirmed the e-Factura user (dgiPortalUserConfirmed — for scratch that is
  // purely the DGI email confirmation, no automation shortcut) AND the user has
  // attested they finished the steps do we register the homologación and hand
  // it to the team (which parks the wizard on the pending screen). A brand-new
  // emisor is never registered on the checkbox alone.
  const scratchHandoffFiredRef = useRef(false);
  useEffect(() => {
    if (!isScratch) return;
    if (!stepsCompletedConfirmed || !dgiPortalUserConfirmed) return;
    if (scratchHandoffFiredRef.current) return;
    scratchHandoffFiredRef.current = true;
    void handleScratchManualDone();
  }, [
    isScratch,
    stepsCompletedConfirmed,
    dgiPortalUserConfirmed,
    handleScratchManualDone,
  ]);

  // The manual "create the e-Factura user yourself" instructions need a real
  // paste-ready password in the credentials card. This fires on entry whenever
  // those instructions show — scratch (always manual) AND an existing emisor
  // who took the manual path — and calls the IDEMPOTENT createDgiPortalUser: it
  // returns the stored password when a user already exists (so a returning
  // existing emisor sees their real password, not a blank), else creates the
  // row (which the DGI-confirmation poll keys off). Fires once; skips once we
  // already have a password to display.
  const credsGenFiredRef = useRef(false);
  useEffect(() => {
    if (!showManualInstructions) return;
    if (!sessionHydrated) return;
    if (generatedPassword) return; // already have a password to display
    if (!portalUserCedula) return; // no CI → the credentials card prompts to complete the profile
    if (saving) return;
    if (credsGenFiredRef.current) return;
    credsGenFiredRef.current = true;
    void (async () => {
      setSaving(true);
      setSavingAction("automation");
      try {
        const { password } = await createDgiPortalUser();
        persistPassword(password);
        onPasswordReady?.();
        await onRefreshTenant?.();
      } catch {
        credsGenFiredRef.current = false; // allow a retry on next render
        reportBlocked("dgi_portal_user_create_failed");
        toast.error(t("onboarding.dgiAccessError"));
      } finally {
        setSaving(false);
        setSavingAction(null);
      }
    })();
  }, [
    showManualInstructions,
    sessionHydrated,
    generatedPassword,
    portalUserCedula,
    saving,
    persistPassword,
    onPasswordReady,
    onRefreshTenant,
    reportBlocked,
    t,
  ]);

  const handleAutomationFailed = (): void => {
    setManualFallback(true);
    onStepsCompletedConfirmed?.(false);
  };

  const handleBackToChoice = (): void => {
    setManualFallback(false);
    onStepsCompletedConfirmed?.(false);
  };

  const handleRegeneratePortalPassword = async (): Promise<void> => {
    if (!hasDgiPortalUser) return;
    setSaving(true);
    setSavingAction("regenerate");
    try {
      // Explicit regenerate — the only path allowed to issue a new password for
      // an existing portal user (the endpoint is idempotent otherwise).
      const { password } = await createDgiPortalUser({ regenerate: true });
      persistPassword(password);
      onPasswordReady?.();
      await onRefreshTenant?.();
      toast.success(t("onboarding.dgiAccessRegeneratePasswordSuccess"));
    } catch {
      reportBlocked("dgi_password_regenerate_failed");
      toast.error(t("onboarding.dgiAccessError"));
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  };

  const copyToClipboard = (text: string, successKey: string): void => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(t(successKey)),
      () => toast.error(t("onboarding.dgiAccessError")),
    );
  };

  const canLeaveDgiStep = dgiPortalUserConfirmed && dgiStep3SuccessToastAcknowledged;

  // ── Fully-done state (all 4 substeps complete, including after refresh) ─
  // Distinct from the partial-confirmed branch below: the whole chain ran
  // end-to-end. Modo is now done; the trail advances to Configuración
  // (certificate upload) which is the next thing the user actually has to do.
  //
  // We accept either the in-session `stepsCompletedConfirmed` flag OR the
  // DB-derived `allAutoStepsDone` signal. On a refresh, the former isn't
  // hydrated yet on the first render, but the latter is — so we render the
  // done screen immediately instead of briefly flashing the partial-confirmed
  // branch before sessionStorage catches up.
  if (
    dgiPortalUserConfirmed &&
    (stepsCompletedConfirmed || allAutoStepsDone) &&
    !choseAutomatic
  ) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t("onboarding.dgiAccessStep.tagDone", "Listo")}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <div className="fx-onb-done-hero">
              <span className="fx-onb-done-ring" aria-hidden>
                <Check />
              </span>
              <span className="fx-onb-modal-eyebrow">
                {t(
                  "onboarding.dgiAccessStep.doneEyebrow",
                  "Configuración completa",
                )}
              </span>
              <h1 className="fx-onb-h1" style={{ textAlign: "center" }}>
                {t(
                  "onboarding.dgiAccessStep.doneH1Lead",
                  "Listo. DGI está",
                )}{" "}
                <span className="ital">
                  {t(
                    "onboarding.dgiAccessStep.doneH1Accent",
                    "configurado.",
                  )}
                </span>
              </h1>
              <p className="fx-onb-lead" style={{ textAlign: "center" }}>
                {t(
                  "onboarding.dgiAccessStep.doneLead",
                  "Sincronizamos tu usuario e-Factura, los CAEs y los últimos números utilizados, y registramos a Fiscalo como tu proveedor de factura electrónica. Falta subir tu certificado digital para empezar a emitir.",
                )}
              </p>
            </div>
            <div className="fx-onb-actions" style={{ marginTop: 12 }}>
              <div className="side" />
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onContinue}
                >
                  {t(
                    "onboarding.dgiAccessStep.doneCta",
                    "Subir certificado",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.dgiAccessStep.brandEyebrowDoneFull",
              "Conexión DGI lista · falta el certificado",
            )}
            quote={
              <>
                {t(
                  "onboarding.dgiAccessStep.brandQuoteDoneFullLead",
                  "Tu acceso a DGI está sincronizado.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.dgiAccessStep.brandQuoteDoneFullAccent",
                    "Subí tu certificado y emitís tu primera factura.",
                  )}
                </span>
              </>
            }
            currentStep={isScratch ? 2 : isCertBeforeDgi ? 4 : 3}
            trailSteps={dgiConfigTrailSteps}
          />
        }
      />
    );
  }

  // ── In-flight automation (resume prompt) ─────────────────────────────
  // Replaces the old auto-resume effect: on a mid-flow refresh we surface
  // an explicit "Reanudar configuración" card so the user has to authorize
  // the chain to continue (previously we'd silently re-fire `flow.start()`
  // which made finished steps flash through "running → success" before
  // settling on the actually-pending one).
  if (hasInFlightAutomation) {
    const progress = dgiAutoOnboardingProgress ?? {};
    const doneCount = [
      progress.portal_user,
      progress.company_info,
      progress.cae_download,
      progress.proveedor_software,
    ].filter(Boolean).length;
    const remainingCount = 4 - doneCount;
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t(
                "onboarding.dgiAccessStep.tagResume",
                "Configuración en curso",
              )}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.dgiAccessStep.resumeH1Lead",
                "Tu configuración quedó",
              )}{" "}
              <span className="ital">
                {t(
                  "onboarding.dgiAccessStep.resumeH1Accent",
                  "a mitad de camino.",
                )}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {remainingCount === 1
                ? t(
                    "onboarding.dgiAccessStep.resumeLeadOne",
                    "Ya tenemos {{done}} de 5 pasos listos. Continuá para que terminemos el último — vamos a saltearnos lo que ya está hecho.",
                    { done: doneCount },
                  )
                : t(
                    "onboarding.dgiAccessStep.resumeLeadMany",
                    "Ya tenemos {{done}} de 5 pasos listos. Continuá para que terminemos los {{remaining}} restantes — vamos a saltearnos lo que ya está hecho.",
                    { done: doneCount, remaining: remainingCount },
                  )}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 16,
                marginBottom: 32,
              }}
            >
              {(
                [
                  ["portal_user", "Usuario e-Factura"],
                  ["company_info", "Datos de la empresa"],
                  ["cae_download", "Solicitud y descarga de CAEs"],
                  ["proveedor_software", "Configurar Fiscalo como tu proveedor de e-Factura"],
                ] as Array<[keyof typeof progress, string]>
              ).map(([key, label]) => {
                const done = Boolean(progress[key]);
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "1px solid var(--fx-s-hairline)",
                      background: done
                        ? "var(--fx-accent-50, rgba(45,97,73,0.05))"
                        : "white",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background: done
                          ? "var(--fx-accent-500)"
                          : "var(--fx-ink-100)",
                        color: done ? "white" : "var(--fx-ink-500)",
                        flexShrink: 0,
                      }}
                    >
                      {done ? (
                        <Check style={{ width: 14, height: 14 }} />
                      ) : (
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "currentColor",
                            display: "inline-block",
                          }}
                        />
                      )}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        color: done
                          ? "var(--fx-ink-900)"
                          : "var(--fx-ink-500)",
                        fontWeight: done ? 600 : 400,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontFamily: "var(--fx-ff-mono)",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: done
                          ? "var(--fx-accent-700)"
                          : "var(--fx-ink-400)",
                      }}
                    >
                      {done
                        ? t("onboarding.autoFlow.statusDone", "Listo")
                        : t(
                            "onboarding.autoFlow.statusPending",
                            "Pendiente",
                          )}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="fx-onb-actions">
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() => {
                    setManualFallback(true);
                    onStepsCompletedConfirmed?.(false);
                  }}
                >
                  {firstIncompleteAutoStep === "portal_user"
                    ? t(
                        "onboarding.dgiAccessStep.resumeSwitchToManual",
                        "Continuar manualmente",
                      )
                    : t(
                        "onboarding.dgiAccessStep.resumeSwitchToHandoff",
                        "Que Fiscalo lo termine",
                      )}
                </button>
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={handleResumeAutomation}
                >
                  {t(
                    "onboarding.dgiAccessStep.resumeCta",
                    "Continuar configuración",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={<AutoFlowBrandColContent flow={flow} />}
      />
    );
  }

  // ── Confirmed success state (portal user only — partial) ─────────────
  // Must check `!manualFallback`: when the auto-flow stalled mid-chain
  // and the user opted into the handoff path, we want the handoff
  // branch (below) to render — not this "DGI confirmó tu usuario"
  // celebration, which would short-circuit the handoff entirely.
  // Scratch: DGI confirmed the e-Factura user AND the user attested the steps.
  // The effect above has fired the homologación registration; show a brief
  // "validado" state while it completes — the parent's needsHomologacionPending
  // gate then swaps in the "homologación pendiente" screen. Scratch does NOT
  // continue to the certificate here (cert + CAE are post-homologación).
  if (isScratch && stepsCompletedConfirmed && dgiPortalUserConfirmed) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t("onboarding.dgiAccessStep.tagDone", "Listo")}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <div className="fx-onb-success-hero">
              <span className="ring" aria-hidden>
                <Check />
              </span>
              <h1 className="fx-onb-h1">
                {t(
                  "onboarding.dgiAccessStep.scratchValidatedTitle",
                  "Usuario validado con DGI.",
                )}
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "onboarding.dgiAccessStep.scratchValidatedMessage",
                  "Estamos preparando tu homologación. Un momento…",
                )}
              </p>
            </div>
            <div className="fx-onb-actions">
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  disabled
                >
                  <Loader2 className="animate-spin" />
                  {t("onboarding.dgiAccessStep.scratchValidatedCta", "Preparando…")}
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.dgiAccessStep.brandEyebrowDone",
              "Listo · sin pasos pendientes",
            )}
            quote={
              <>
                {t(
                  "onboarding.dgiAccessStep.brandQuoteDoneLead",
                  "Tu usuario e-Factura está activo.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.dgiAccessStep.scratchValidatedAccent",
                    "Ahora la homologación corre por nuestra cuenta.",
                  )}
                </span>
              </>
            }
            currentStep={dgiTrailCurrent}
            trailSteps={dgiConfigTrailSteps}
          />
        }
      />
    );
  }

  if (
    dgiPortalUserConfirmed &&
    !choseAutomatic &&
    !manualFallback &&
    !isScratch
  ) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t("onboarding.dgiAccessStep.tagDone", "Listo")}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <div className="fx-onb-success-hero">
              <span className="ring" aria-hidden>
                <Check />
              </span>
              <h1 className="fx-onb-h1">
                {t("onboarding.dgiAccessConfirmedTitle")}
              </h1>
              <p className="fx-onb-lead">
                {t("onboarding.dgiAccessConfirmedMessage")}
              </p>
              <p className="fx-onb-lead" style={{ marginTop: -8 }}>
                {t("onboarding.dgiAccessConfirmedDetail")}
              </p>
            </div>
            <div className="fx-onb-actions">
              <div className="side">
                {onBack ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onBack}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                ) : null}
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onContinue}
                  disabled={!canLeaveDgiStep}
                >
                  {t("onboarding.next", "Continuar")}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.dgiAccessStep.brandEyebrowDone",
              "Listo · sin pasos pendientes",
            )}
            quote={
              <>
                {t(
                  "onboarding.dgiAccessStep.brandQuoteDoneLead",
                  "Tu usuario e-Factura está activo.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.dgiAccessStep.brandQuoteDoneAccent",
                    "Seguimos con el certificado.",
                  )}
                </span>
              </>
            }
            currentStep={dgiTrailCurrent}
            trailSteps={dgiConfigTrailSteps}
          />
        }
      />
    );
  }

  // ── Manual fallback: handoff to Fiscalo (steps 2-5) ──────────────────
  // We only offer DIY manual instructions for portal_user — every other
  // auto step (company_info / cae_download / last_numbers /
  // proveedor_software) is too involved to expect a regular user to
  // complete in DGI. When one of those fails we hand off to the Fiscalo
  // team: the backend has already notified ops via the retry policy, so
  // we just need to reassure the user and let them keep moving through
  // the rest of the onboarding.
  if (
    showManualInstructions &&
    firstIncompleteAutoStep &&
    firstIncompleteAutoStep !== "portal_user"
  ) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t(
                "onboarding.dgiAccessStep.tagHandoff",
                "Modo · Fiscalo se encarga",
              )}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.dgiAccessStep.handoffH1Lead",
                "Nuestro equipo termina la",
              )}{" "}
              <span className="ital">
                {t(
                  "onboarding.dgiAccessStep.handoffH1Accent",
                  "configuración.",
                )}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.dgiAccessStep.handoffLead",
                "Tu usuario e-Factura ya está creado. Los pasos que quedan en DGI son técnicos y los completa Fiscalo por vos — el equipo ya fue notificado y se va a encargar.",
              )}
            </p>

            <div
              style={{
                marginTop: 12,
                padding: "14px 16px",
                borderRadius: 10,
                border: "1px solid var(--fx-s-hairline)",
                background: "var(--fx-accent-50, rgba(45,97,73,0.04))",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "var(--fx-accent-500)",
                  color: "white",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Check style={{ width: 14, height: 14 }} />
              </span>
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--fx-s-text)",
                  }}
                >
                  {t(
                    "onboarding.dgiAccessStep.handoffNoticeTitle",
                    "Avisamos al equipo de Fiscalo",
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--fx-ink-500)",
                    marginTop: 2,
                  }}
                >
                  {t(
                    "onboarding.dgiAccessStep.handoffNoticeBody",
                    "Vamos a terminar de configurar tu cuenta en DGI y te escribimos cuando esté lista. Mientras tanto, podés seguir con los próximos pasos del onboarding.",
                  )}
                </div>
              </div>
            </div>

            <div className="fx-onb-actions" style={{ marginTop: 16 }}>
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={handleBackToChoice}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  disabled={blockScratchHandoff}
                  title={
                    blockScratchHandoff
                      ? t(
                          "onboarding.dgiAccessStep.scratchMustCreateUser",
                          "Primero creá tu usuario de e-Factura para continuar.",
                        )
                      : undefined
                  }
                  onClick={() => {
                    if (blockScratchHandoff) return;
                    onStepsCompletedConfirmed?.(true);
                    onContinue();
                  }}
                >
                  {t(
                    "onboarding.dgiAccessStep.handoffCta",
                    "Seguir con el onboarding",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.dgiAccessStep.brandEyebrowHandoff",
              "Fiscalo termina · vos seguís",
            )}
            quote={
              <>
                {t(
                  "onboarding.dgiAccessStep.brandQuoteHandoffLead",
                  "Lo técnico lo cerramos nosotros.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.dgiAccessStep.brandQuoteHandoffAccent",
                    "Vos avanzás con el certificado mientras tanto.",
                  )}
                </span>
              </>
            }
            currentStep={isScratch ? 2 : isCertBeforeDgi ? 4 : 3}
            trailSteps={dgiConfigTrailSteps}
          />
        }
      />
    );
  }

  // ── Manual instructions mode (portal_user — create the user) ─────────
  // Once the user attests they finished (stepsCompletedConfirmed) but DGI
  // hasn't confirmed the e-Factura user yet, yield to the "Esperando
  // confirmación de DGI" branch below so scratch (and any manual creator) is
  // gated on the real DGI validation before advancing/handoff.
  if (showManualInstructions && !showWaitingForDgiEmail) {
    return (
      <OnboardingShell
        variant="wide"
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t(
                "onboarding.dgiAccessStep.tagManual",
                "Configuración DGI · Manual",
              )}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.dgiAccessStep.manualH1Lead",
                "Creá el usuario en",
              )}{" "}
              <span className="ital">
                {t("onboarding.dgiAccessStep.manualH1Accent", "DGI.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.dgiAccessStep.manualLead",
                "Seguí los pasos en el portal de DGI. Usá las credenciales que armamos para vos — están a la derecha, listas para copiar.",
              )}
            </p>

            <ol className="fx-onb-dgi-steps">
              <li>
                {t("onboarding.dgiAccessCreateStep1Prefix")}{" "}
                <a
                  href={DGI_SERVICIOS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("onboarding.dgiAccessCreateStep1Link")}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>{t("onboarding.dgiAccessCreateStep2")}</li>
              <li>
                {t("onboarding.dgiAccessCreateStep3")}
                <ul className="substeps">
                  <li>{t("onboarding.dgiAccessCreateStep3_1")}</li>
                </ul>
              </li>
              <li>{t("onboarding.dgiAccessCreateStep4AfterBox")}</li>
              <li>
                {t("onboarding.dgiAccessCreateStep5Prefix")}
                <div className="fx-onb-roles">
                  <div className="role">
                    <span className="check" aria-hidden>
                      <Check />
                    </span>
                    <strong>{t("onboarding.dgiAccessCreateStep5Role1")}</strong>
                  </div>
                  {isExistingEmisor && (
                    <div className="role">
                      <span className="check" aria-hidden>
                        <Check />
                      </span>
                      <strong>
                        {t("onboarding.dgiAccessStep.roleProduccion", "Producción")}
                      </strong>
                    </div>
                  )}
                  <span className="note">
                    {isExistingEmisor
                      ? t(
                          "onboarding.dgiAccessStep.rolesNoteExisting",
                          "Marcá ambos roles: Homologación simplificada y Producción. Ya sos emisor, así que no hay una homologación posterior.",
                        )
                      : t(
                          "onboarding.dgiAccessStep.rolesNoteHomologacion",
                          "Por ahora marcá solo Homologación simplificada. El rol de Producción se agrega después, cuando terminemos la homologación.",
                        )}
                  </span>
                </div>
              </li>
            </ol>

            <button
              type="button"
              className={cn("fx-onb-consent", stepsCompletedConfirmed && "checked")}
              onClick={() =>
                onStepsCompletedConfirmed?.(!stepsCompletedConfirmed)
              }
              aria-pressed={stepsCompletedConfirmed}
              style={{ marginTop: 16 }}
            >
              <span className="ckbox" aria-hidden>
                <Check />
              </span>
              <span className="body">
                <span className="txt">
                  {t("onboarding.dgiAccessStepsDoneCheckbox")}
                </span>
              </span>
            </button>

            <div className="fx-onb-actions">
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() =>
                    isScratch ? onBack?.() : handleBackToChoice()
                  }
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              </div>
              <div className="side end">
                {/*
                  For scratch, ticking the "hice todos los pasos" checkbox is
                  the commit: it routes to the "Esperando confirmación de DGI"
                  branch (validation gate), and the homologación is registered
                  by the effect once DGI confirms — never from this button. So
                  this button only drives the non-scratch manual-fallback path.
                */}
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onContinue}
                  disabled={!stepsCompletedConfirmed}
                >
                  {t("onboarding.next", "Continuar")}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <div className="fx-onb-brand-col">
            <div className="fx-onb-creds">
              <span className="fx-onb-creds-eyebrow">
                {t(
                  "onboarding.dgiAccessStep.credsEyebrow",
                  "Tus credenciales · paste-ready",
                )}
              </span>
              <h2 className="fx-onb-creds-title">
                {t("onboarding.dgiAccessStep.credsTitleLead", "Pegá estos datos en")}{" "}
                <span className="ital">
                  {t("onboarding.dgiAccessStep.credsTitleAccent", "DGI.")}
                </span>
              </h2>
              <p className="fx-onb-creds-lead">
                {t(
                  "onboarding.dgiAccessStep.credsLead",
                  "Tocá copiar y pegá en cada campo del formulario.",
                )}
              </p>

              <CredentialField
                k={t("onboarding.dgiAccessCreateStep4DocType")}
                v={DGI_EFACTURA_PORTAL_USER_DOC_TYPE}
                onCopy={() =>
                  copyToClipboard(
                    DGI_EFACTURA_PORTAL_USER_DOC_TYPE,
                    "onboarding.dgiAccessCopied",
                  )
                }
                successKey="onboarding.dgiAccessCopied"
              />
              <CredentialField
                k={t("onboarding.dgiAccessCreateStep4DocNumber")}
                v={
                  portalUserCedula ||
                  t(
                    "onboarding.dgiAccessCreateStep4DocNumberMissing",
                    "Falta tu cédula en el perfil",
                  )
                }
                onCopy={() => {
                  if (!portalUserCedula) return;
                  copyToClipboard(portalUserCedula, "onboarding.dgiAccessCopiedDocumentNumber");
                }}
                successKey="onboarding.dgiAccessCopiedDocumentNumber"
              />
              <CredentialField
                k={t("onboarding.dgiAccessCreateStep4EmailLabel")}
                v={DGI_NOTIFICATIONS_EMAIL}
                onCopy={() =>
                  copyToClipboard(
                    DGI_NOTIFICATIONS_EMAIL,
                    "onboarding.dgiAccessCopiedEmail",
                  )
                }
                successKey="onboarding.dgiAccessCopiedEmail"
              />
              {generatedPassword ? (
                <CredentialField
                  k={t("onboarding.dgiAccessPasswordLabel")}
                  v={generatedPassword}
                  hide
                  visible={passwordVisible}
                  toggleVisible={() => setPasswordVisible((v) => !v)}
                  onCopy={() =>
                    copyToClipboard(generatedPassword, "onboarding.dgiAccessCopied")
                  }
                  successKey="onboarding.dgiAccessCopied"
                />
              ) : hasDgiPortalUser ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  style={{
                    color: "var(--fx-accent-300)",
                    borderColor: "rgba(111,165,133,0.30)",
                  }}
                  disabled={saving}
                  onClick={() => void handleRegeneratePortalPassword()}
                >
                  {saving && savingAction === "regenerate" ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <RefreshCw aria-hidden />
                  )}
                  {t("onboarding.dgiAccessRegeneratePassword")}
                </button>
              ) : null}

              <div className="fx-onb-creds-foot" data-mag-private>
                <span className="dot" aria-hidden />
                {t(
                  "onboarding.dgiAccessStep.credsFoot",
                  legalName
                    ? `Generadas para ${legalName} · expiran al cerrar este flujo`
                    : "Generadas solo para esta empresa · expiran al cerrar este flujo",
                  { legalName },
                )}
              </div>
            </div>
          </div>
        }
      />
    );
  }

  // ── Waiting for DGI email confirmation (post-manual confirmation) ────
  if (showWaitingForDgiEmail) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={dgiTagStep}
              total={dgiConfigTotal}
              label={t("onboarding.dgiAccessStep.tagWaiting", "Esperando DGI")}
              flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.dgiAccessStep.waitingH1Lead",
                "Esperando confirmación de",
              )}{" "}
              <span className="ital">
                {t("onboarding.dgiAccessStep.waitingH1Accent", "DGI.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t("onboarding.dgiAccessWaitingMessage")}
            </p>
            <div className="fx-onb-actions">
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={handleBackToChoice}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  disabled
                >
                  <Loader2 className="animate-spin" />
                  {t("onboarding.dgiAccessStep.waitingCta", "Esperando…")}
                </button>
              </div>
            </div>
          </>
        }
        brand={
          <OnboardingBrandColumn
            eyebrow={t(
              "onboarding.dgiAccessStep.brandEyebrowWaiting",
              "Llegando · sin acción de tu parte",
            )}
            quote={
              <>
                {t(
                  "onboarding.dgiAccessStep.brandQuoteWaitingLead",
                  "DGI nos confirma por correo.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.dgiAccessStep.brandQuoteWaitingAccent",
                    "Te avisamos apenas llegue.",
                  )}
                </span>
              </>
            }
            currentStep={dgiTrailCurrent}
            trailSteps={dgiConfigTrailSteps}
          />
        }
      />
    );
  }

  // ── Auto-flow running / done (inline, replaces the legacy modal) ────
  if (choseAutomatic && flow.phase !== "idle") {
    // Working state intentionally omits a status label — the auto-flow's
    // own "Configurando · X de Y" eyebrow already conveys progress, so
    // repeating it in the step tag reads as redundant.
    const tagLabel =
      flow.phase === "done"
        ? t("onboarding.dgiAccessStep.tagDone", "Listo")
        : flow.hasFailed
          ? t("onboarding.dgiAccessStep.tagFailed", "Falló")
          : undefined;
    return (
      <>
        <OnboardingShell
          form={
            <>
              <OnboardingBrandMark />
              <OnboardingStepTag
                step={dgiTagStep}
                total={dgiConfigTotal}
                label={tagLabel}
                flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
              />
              <AutoFlowFormColContent
                flow={flow}
                onContinue={onContinue}
                onAbort={() => {
                  // User bails on the automatic flow — fall back to the manual
                  // path with paste-ready credentials. The backend orchestration
                  // session is just abandoned (no rollback needed; nothing has
                  // been written yet at this stage).
                  flow.setPortalUserModalOpen(false);
                  setChoseAutomatic(false);
                  setManualFallback(true);
                  onStepsCompletedConfirmed?.(false);
                }}
              />
            </>
          }
          brand={<AutoFlowBrandColContent flow={flow} />}
        />
        <DgiConsultaFlowModal
          open={flow.portalUserModalOpen}
          onOpenChange={(isOpen) => {
            flow.setPortalUserModalOpen(isOpen);
            if (!isOpen) {
              // Delegate to the hook — it knows whether the close was driven
              // by the post-SSO success path (in which case we DON'T want to
              // tear down the still-running Python automation) or a real
              // user-cancel / failure.
              void flow.handlePortalUserModalClosed();
            }
          }}
          documentKey="efactura-portal-user"
          documentTitle={t(
            "onboarding.autoFlow.ssoModalTitle",
            "Autenticación con DGI",
          )}
          variant="portal-user"
          onFlowSuccess={(payload) =>
            void flow.handlePortalUserSuccess(payload?.sessionId)
          }
          onPortalAutomationFailed={() => void flow.handlePortalUserFailed()}
        />
        <DgiConsultaFlowModal
          open={flow.ssoRefreshModalOpen}
          onOpenChange={(isOpen) => {
            flow.setSsoRefreshModalOpen(isOpen);
            if (!isOpen) {
              // Closed without explicit success → treat as re-auth failed.
              void flow.handleSsoRefreshFailed();
            }
          }}
          documentKey="sso-refresh"
          documentTitle={t(
            "onboarding.autoFlow.ssoRefreshModalTitle",
            "Reautenticación con DGI",
          )}
          variant="sso-refresh"
          onFlowSuccess={() => void flow.handleSsoRefreshSuccess()}
          onPortalAutomationFailed={() => void flow.handleSsoRefreshFailed()}
        />
      </>
    );
  }

  // ── Entry: single-CTA landing for the auto-flow ──────────────────────
  // We used to offer an upfront "auto vs manual" choice here; the manual
  // path required the user to operate the DGI portal themselves end-to-
  // end, which most users didn't actually want. We now always run the
  // auto-flow and only surface the manual instructions screen as a
  // fallback when SSO authentication with DGI fails (portal_user step) —
  // see the showManualInstructions branch above.
  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={dgiTagStep}
            total={dgiConfigTotal}
            label={t(
              "onboarding.dgiAccessStep.tagChoice",
              "Configuración DGI",
            )}
            flow={t("onboarding.dgiAccessStep.flow", "Configuración DGI")}
          />
          <h1 className="fx-onb-h1">
            {t(
              "onboarding.dgiAccessStep.choiceH1Lead",
              "Configurá Fiscalo como tu proveedor de",
            )}{" "}
            <span className="ital">
              {t(
                "onboarding.dgiAccessStep.choiceH1Accent",
                "e-Factura.",
              )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.dgiAccessStep.choiceLead",
              "Necesitamos crear tu usuario en DGI y configurar los roles. ¿Cómo querés hacerlo?",
            )}
          </p>

          <div className="fx-onb-choice-grid">
            <OnboardingChoiceCard
              icon={<Sparkles aria-hidden />}
              name={
                <>
                  {t(
                    "onboarding.dgiAccessStep.autoNameLead",
                    "Lo hacemos",
                  )}{" "}
                  <span className="ital">
                    {t("onboarding.dgiAccessStep.autoNameAccent", "nosotros.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.dgiAccessStep.autoDesc",
                "Creamos el usuario en DGI, configuramos los roles y dejamos todo listo. Vos solo autorizás el proceso una vez con tu acceso a DGI.",
              )}
              meta={
                <span className="meta-outcome">
                  {t("onboarding.dgiAccessStep.autoOutcome", "Un click")}
                </span>
              }
              ribbon={t("onboarding.dgiAccessStep.recommended", "Recomendado")}
              selected={dgiSetupChoice === "auto"}
              onClick={() => setDgiSetupChoice("auto")}
            />
            <OnboardingChoiceCard
              icon={<Settings aria-hidden />}
              name={
                <>
                  {t("onboarding.dgiAccessStep.manualNameLead", "Lo hago")}{" "}
                  <span className="ital">
                    {t("onboarding.dgiAccessStep.manualNameAccent", "yo.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.dgiAccessStep.manualDesc",
                "Te damos los pasos exactos para crear el usuario y los roles en el portal de DGI, con las credenciales listas para copiar.",
              )}
              meta={
                <span className="meta-outcome">
                  {t(
                    "onboarding.dgiAccessStep.manualOutcome",
                    "Vos en el portal DGI, paso a paso",
                  )}
                </span>
              }
              selected={dgiSetupChoice === "manual"}
              onClick={() => setDgiSetupChoice("manual")}
            />
          </div>

          <div className="fx-onb-actions">
            <div className="side">
              {onBack ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={onBack}
                  disabled={saving}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              ) : null}
            </div>
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={saving}
                onClick={() => {
                  if (dgiSetupChoice === "manual") {
                    setManualFallback(true);
                  } else {
                    void handleStartAutomation();
                  }
                }}
              >
                {saving ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {dgiSetupChoice === "manual"
                      ? t(
                          "onboarding.dgiAccessStep.manualCta",
                          "Ver los pasos",
                        )
                      : t(
                          "onboarding.dgiAccessStep.autoCta",
                          "Autorizar y configurar",
                        )}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.dgiAccessStep.brandEyebrowChoice",
            "Lo hacemos por vos",
          )}
          quote={
            <>
              {t(
                "onboarding.dgiAccessStep.brandQuoteAutoLead",
                "Un click y lo dejamos listo.",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.dgiAccessStep.brandQuoteAutoAccent",
                  "Si algo falla, lo arreglamos nosotros.",
                )}
              </span>
            </>
          }
          currentStep={dgiTrailCurrent}
          trailSteps={dgiConfigTrailSteps}
        />
      }
    />
  );
}
